import axios, { type InternalAxiosRequestConfig } from "axios";
import { msalInstance, loginRequest } from "../auth/msalConfig";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { VITE_API_URL } from "../config/env";

/**
 * Axios instance configured for the QB Dashboard API.
 * In development, baseURL is empty so Vite's proxy routes /api/* to localhost:8000.
 * In production, VITE_API_URL should point to the deployed backend.
 */
export const apiClient = axios.create({
  baseURL: VITE_API_URL,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 30_000,
});

/**
 * Request interceptor: attaches the MSAL Bearer token to every request.
 * Falls back to interactive token acquisition if the silent request fails.
 */
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> => {
    const accounts = msalInstance.getAllAccounts();

    if (accounts.length === 0) {
      // No authenticated user — let the request through unauthenticated;
      // the server will return 401 and the UI will redirect to login.
      return config;
    }

    const activeAccount = accounts[0]!;

    try {
      const tokenResponse = await msalInstance.acquireTokenSilent({
        ...loginRequest,
        account: activeAccount,
      });

      config.headers.set("Authorization", `Bearer ${tokenResponse.idToken}`);
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        // Silent token acquisition failed — trigger interactive redirect.
        await msalInstance.acquireTokenRedirect({
          ...loginRequest,
          account: activeAccount,
        });
      }
      // For other errors, proceed without a token so the server returns 401.
    }

    return config;
  },
  (error: unknown) => Promise.reject(error)
);

/**
 * Response interceptor: surface structured error information.
 * Does NOT redirect on 401 — let MSAL handle auth state instead
 * of fighting with the redirect flow.
 */
apiClient.interceptors.response.use(
  (response) => response,
  (error: unknown) => {
    return Promise.reject(error);
  }
);
